import React from 'react';
import { MessageSquare, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * AIBotPanel — generic admin panel for an AI bot job (AI Book Reviewer,
 * AI Book Discussion). Both used to be ~85-line copy-paste blocks in
 * AdminSettings.jsx that differed only in colors, copy, the toggle
 * handler, and the recent-activity renderer.
 *
 * Extracted from AdminSettings.jsx (2026-05-29).
 *
 * Props:
 *   title            — e.g. "AI Book Reviewer"
 *   color            — "purple" | "cyan" (drives accent BG / text)
 *   testIdPrefix     — e.g. "ai-reviewer" → testIds are derived from it
 *   status           — object: { enabled, total, last_run, recent_items }
 *   totalLabel       — e.g. "Total AI Reviews"
 *   description      — short paragraph shown when bot is enabled
 *   actionLabel      — primary CTA text, e.g. "Generate Reviews Now"
 *   actionHint       — small caption under the CTA
 *   loadingLabel     — CTA loading text, e.g. "Generating Reviews..."
 *   toggling         — bool, disables toggle while in-flight
 *   running          — bool, disables CTA + shows spinner
 *   offMessage       — paragraph shown when bot is disabled
 *   runTestId        — explicit data-testid for the run button (defaults to `${testIdPrefix}-run-btn`)
 *   renderItem(item) — render a single row in recent-activity list (item shape varies per bot)
 *   onToggle         — toggle on/off handler
 *   onRun            — primary CTA handler
 */
const AIBotPanel = ({
  title,
  color = 'purple',
  testIdPrefix,
  status,
  totalLabel,
  description,
  actionLabel,
  actionHint,
  loadingLabel,
  toggling = false,
  running = false,
  offMessage,
  runTestId,
  renderItem,
  onToggle,
  onRun,
  extraControls,
}) => {
  const accent = {
    purple: { bg: 'bg-purple-600', hover: 'hover:bg-purple-700', text: 'text-purple-400', icon: 'text-purple-500' },
    cyan:   { bg: 'bg-cyan-600',   hover: 'hover:bg-cyan-700',   text: 'text-cyan-400',   icon: 'text-cyan-500' },
  }[color] || { bg: 'bg-purple-600', hover: 'hover:bg-purple-700', text: 'text-purple-400', icon: 'text-purple-500' };

  const enabled = status?.enabled !== false;
  const totalCount = status?.total ?? 0;
  const lastRun = status?.last_run;
  const recentItems = status?.recent_items || [];

  return (
    <div className="bg-white/5 rounded-xl border border-white/10 p-6" data-testid={`${testIdPrefix}-section`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <MessageSquare className={`h-6 w-6 ${accent.icon} mr-2`} />
          <h2 className="text-xl font-bold text-white">{title}</h2>
        </div>
        <button
          onClick={onToggle}
          disabled={toggling}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
            enabled ? accent.bg : 'bg-gray-600'
          }`}
          data-testid={`toggle-${testIdPrefix}`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>

      {!enabled ? (
        <div className="text-center py-6">
          <MessageSquare className="h-10 w-10 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">{title} is turned off</p>
          <p className="text-gray-600 text-xs mt-1">{offMessage}</p>
        </div>
      ) : (
        <>
          <p className="text-gray-400 text-sm mb-4">{description}</p>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-white/5 rounded-lg p-3 text-center">
              <p className={`text-2xl font-bold ${accent.text}`}>{totalCount}</p>
              <p className="text-xs text-gray-500">{totalLabel}</p>
            </div>
            <div className="bg-white/5 rounded-lg p-3 text-center">
              <p className="text-sm font-medium text-gray-300">
                {lastRun ? new Date(lastRun.run_at).toLocaleDateString() : 'Never'}
              </p>
              <p className="text-xs text-gray-500">Last Run</p>
            </div>
          </div>

          {recentItems.length > 0 && (
            <div className="mb-4 max-h-40 overflow-y-auto space-y-2">
              {recentItems.slice(0, 5).map(renderItem)}
            </div>
          )}

          {extraControls}

          <Button
            onClick={onRun}
            disabled={running}
            className={`w-full ${accent.bg} ${accent.hover} text-white`}
            data-testid={runTestId || `${testIdPrefix}-run-btn`}
          >
            {running ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {loadingLabel}</>
            ) : (
              <><MessageSquare className="h-4 w-4 mr-2" /> {actionLabel}</>
            )}
          </Button>
          <p className="text-xs text-gray-600 mt-2 text-center">{actionHint}</p>
        </>
      )}
    </div>
  );
};

export default AIBotPanel;
